import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { CategorizedVideos } from '../../services/video.service';
import VideoCard from './VideoCard';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CategorizedVideoListProps {
  categorizedVideos: CategorizedVideos[];
  selectCategory: (categoryId: string) => void;
}

export const CategorizedVideoList: React.FC<CategorizedVideoListProps> = ({
  categorizedVideos,
  selectCategory
}) => {
  return (
    <div className="space-y-8">
      {categorizedVideos.map((category) => (
        <CategorySection key={category._id.name} category={category} selectCategory={selectCategory} />
      ))}
    </div>
  );
};

const CategorySection: React.FC<{
  category: CategorizedVideos, selectCategory?: (categoryId: string) => void
}> = ({ category, selectCategory }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 1,
    breakpoints: {
      '(min-width: 768px)': { slidesToScroll: 3 }
    }
  });
  const [prevEnabled, setPrevEnabled] = useState(false);
  const [nextEnabled, setNextEnabled] = useState(false);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setPrevEnabled(emblaApi.canScrollPrev());
    setNextEnabled(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-juana text-xl font-bold">{category._id.name}</h2>
        <div
          className="text-holos-dark cursor-pointer hover:text-holos-dark/70 flex items-center whitespace-nowrap"
          onClick={() => selectCategory?.(category._id._id)}>
          Ver Más
          <ChevronRight className="inline-block ml-1" size={20} />
        </div>
      </div>

      <div className="relative">
        <div className="embla overflow-hidden" ref={emblaRef}>
          <div className="embla__container flex">
            {category.videos.map((video) => (
              <div key={video._id} className="embla__slide mr-4 min-w-[70%] sm:min-w-[40%] md:min-w-[25%] max-w-[300px]">
                <VideoCard video={video} />
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={scrollPrev}
          disabled={!prevEnabled}
          className={`absolute top-1/2 left-[-30px] transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md ${prevEnabled ? 'cursor-pointer' : 'opacity-30 cursor-default'
            }`}
        >
          <ChevronLeft />
        </button>
        <button
          onClick={scrollNext}
          disabled={!nextEnabled}
          className={`absolute top-1/2 right-[-30px] transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md ${nextEnabled ? 'cursor-pointer' : 'opacity-30 cursor-default'
            }`}
        >
          <ChevronRight />
        </button>
      </div>
    </div>
  );
};